<template>
  <b-modal
    id="add-or-update-patient-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add' }} {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <b-row>
              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" name="Code" rules="required">
                  <b-form-group label-for="code">
                    <template #label>
                      <span>Code<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="code"
                      v-model="icd_10_data.code"
                      placeholder="Code"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="9">
                <validation-provider #default="validationContext" name="Disease" rules="required">
                  <b-form-group label-for="disease">
                    <template #label>
                      <span>Disease<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="disease"
                      v-model="icd_10_data.disease"
                      placeholder="Disease"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                {{ editingData ? 'Update' : 'Save' }} & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  code: "",
  disease: "",
}

export default {
  components: {
    BOverlay,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormTextarea,
    BAlert,
    BCardText,
    vSelect,
    BModal,
    BRow,
    BCol,
    BFormDatepicker,
    BFormSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    isAddActive: {
      type: Boolean,
      required: true,
    },
    editingData: {
      type: [Object, null],
      required: false,
      default: () => null
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      modalTitle: 'ICD-10 Medical Diagnosis Code',
      icd_10_data: cloneDeep(rawData),
    }
  },
  computed: {
    isUpdate(){
      return !!this.editingData
    },
    disableForm(){
      return this.editingData ? !this.can("icd_10_diagnosis_codes:CanUpdate") : !this.can("icd_10_diagnosis_codes:CanAddNew")
    },
  },
  watch: {
    editingData: {
      handler(v){
        if (v){
          this.icd_10_data = v
        } else {
          this.icd_10_data = cloneDeep(rawData)
        }
      }, 
      immediate: true,
      deep: true
    }
  },
  methods: {
    async onCreate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        const is_update = this.icd_10_data.id && this.editingData != null
       
        if (!is_update && !this.can("icd_10_diagnosis_codes:CanAddNew")) {
          return
        }

        if (is_update && !this.can("icd_10_diagnosis_codes:CanUpdate")) {
          return
        }

        if (is_update){
          await this.useJwt().mainService.medicalDiagnosisCodes.update(this.icd_10_data.id, {
            disease: this.icd_10_data.disease,
            code: this.icd_10_data.code
          });
        } else {
          await this.useJwt().mainService.medicalDiagnosisCodes.create(this.icd_10_data);
        }
        
        
        this.$emit("refetchData");
        this.$emit('update:is-add-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `${this.modalTitle} ${is_update ? 'updated' : 'added'} successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.$emit("resetUpdateData")
      this.icd_10_data = {};
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
